import React, { useState, useEffect, useRef } from "react";
import Layout from "../Layout/Layout";
import "../css/assessment.css";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../actions/api";
import { toast } from "react-toastify";

export default function AssessmentView() {
  const { state } = useLocation();
  const assessmentId = state?.id;
  const incompleteId = state?.incomplete_id;
  const assessmentStatus = state?.assessment_status;
  const hasHandledAlready = useRef(false);

  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [sliderValue, setSliderValue] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const sliderRef = useRef(null);
  const labelsContainerRef = useRef(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scores, setScores] = useState({
    totalObtainedScore: 0,
    totalScore: 0,
    percentage: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isFinalQuestion, setIsFinalQuestion] = useState(false);
  const [introduction, setIntroduction] = useState('');
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);

  // Calculate scores when questions change or on results page
  useEffect(() => {
    if (questions.length > 0 && (currentPage === totalPages || isCompleted)) {
      const totalObtainedScore = questions.reduce((sum, question) => {
        let score = 0;

        if (question.answer) {
          if (typeof question.answer === "object") {
            score = question.answer.score || 0;
          } else if (typeof question.answer === "number") {
            score = question.answer;
          }
        }

        return sum + score;
      }, 0);

      const totalScore = questions.length * 12;
      const percentage = Math.round((totalObtainedScore / totalScore) * 100);

      setScores({
        totalObtainedScore,
        totalScore,
        percentage,
      });
    }
  }, [currentPage, questions, totalPages, isCompleted]);

  // Fetch assessment data
  useEffect(() => {
    const fetchAssessment = async (id, incompleteId, assessmentStatus) => {
      try {
        setLoading(true);

        if ((incompleteId && assessmentStatus === 'not-completed') || assessmentStatus === 'retake') {
          let url = `/user/assessments/incomplete-details?user_assessment_score_id=${incompleteId}`
          if (assessmentStatus === 'retake') {
            url = `/user/assessments/details?user_assessment_score_ids[]=${incompleteId}`
          }
          const fetchAssementDetail = await api.get(url);
          if (fetchAssementDetail?.data?.data) {
            if (assessmentStatus === 'retake') {
              setAssessmentData(fetchAssementDetail.data.data[0]);
            } else {
              setAssessmentData(fetchAssementDetail.data.data.user_assessment);
            }
            const fetchedQuestions = assessmentStatus === 'retake' ? fetchAssementDetail.data.data[0].questions : fetchAssementDetail.data.data.user_assessment.questions;

            // Initialize answers properly
            const initializedQuestions = fetchedQuestions.map((q) => {
              let answer = null;

              if (typeof q.answer === "object" && q.answer !== null) {
                answer = {
                  id: q.answer.id || null,
                  user_assessment_score_id:
                    q.answer.user_assessment_score_id || null,
                  score: q.answer.score ?? 0,
                };
              } else if (typeof q.answer === "number") {
                answer = {
                  id: null,
                  user_assessment_score_id: null,
                  score: q.answer,
                };
              } else {
                answer = {
                  id: null,
                  user_assessment_score_id: null,
                  score: 0,
                };
              }

              return {
                ...q,
                answer,
                answered:
                  typeof answer.score === "number" && !isNaN(answer.score),
              };
            });

            setQuestions(initializedQuestions);
            setTotalPages(initializedQuestions.length + 2);

            const answeredIndex = (assessmentStatus === 'retake' ? fetchAssementDetail?.data?.data[0].questions_answered : fetchAssementDetail?.data?.data?.answered_question_index) || 0;
            if (answeredIndex) {
              const answeredQuestion = initializedQuestions[answeredIndex - 1];

              setSliderValue(answeredQuestion?.answer?.score || 0);
              setCurrentPage(answeredIndex + 1);
            } else {
              if (
                currentPage > 1 &&
                currentPage <= initializedQuestions.length + 1
              ) {
                const question = initializedQuestions[currentPage - 2];
                setSliderValue(question.answer?.score || question.answer || 0);
              }
            }
          }
        } else {
          const response = await api.post(`/user/assessment/${id}/start`);

          if (!response?.data?.status) {
            const message =
              response.data?.message || "Assessment already completed.";

            if (!hasHandledAlready.current) {
              hasHandledAlready.current = true;
              setAssessmentCompleted(true);
              toast.error(message);
              navigate("/assessment");
            }
            return;
          }

          if (response?.data?.data) {
            setAssessmentData(response.data.data.user_assessment);
            const fetchedQuestions = response.data.data.user_assessment.questions;

            // Initialize answers properly
            const initializedQuestions = fetchedQuestions.map((q) => {
              let answer = null;

              if (typeof q.answer === "object" && q.answer !== null) {
                answer = {
                  id: q.answer.id || null,
                  user_assessment_score_id:
                    q.answer.user_assessment_score_id || null,
                  score: q.answer.score ?? 0,
                };
              } else if (typeof q.answer === "number") {
                answer = {
                  id: null,
                  user_assessment_score_id: null,
                  score: q.answer,
                };
              } else {
                answer = {
                  id: null,
                  user_assessment_score_id: null,
                  score: 0,
                };
              }

              return {
                ...q,
                answer,
                answered:
                  typeof answer.score === "number" && !isNaN(answer.score),
              };
            });

            setQuestions(initializedQuestions);
            setTotalPages(initializedQuestions.length + 2);

            const answeredIndex =
              response?.data?.data?.answered_question_index || 0;
            if (answeredIndex) {
              const answeredQuestion = initializedQuestions[answeredIndex - 1];

              setSliderValue(answeredQuestion?.answer?.score || 0);
              setCurrentPage(answeredIndex + 1);
            } else {
              if (
                currentPage > 1 &&
                currentPage <= initializedQuestions.length + 1
              ) {
                const question = initializedQuestions[currentPage - 2];
                setSliderValue(question.answer?.score || question.answer || 0);
              }
            }
          }
        }

      } catch (error) {
        console.error("Assessment load error:", error);
        toast.error(
          error?.response?.data?.message || "Failed to load assessment."
        );
        navigate("/assessment");
      } finally {
        setLoading(false);
      }
    };

    const fetchIntroduction = async () => {
      try {
        const response = await api.get(`/user/assessments-introduction`);

        if (response?.data?.data) {
          setIntroduction(response?.data?.data)
        }
      } catch (error) {
        console.error("Assessment load error:", error);
        toast.error(
          error?.response?.data?.message || "Failed to load introduction."
        );
        navigate("/assessment");
      }
    }

    fetchIntroduction();
    fetchAssessment(assessmentId, incompleteId, assessmentStatus);
  }, [assessmentId, incompleteId, assessmentStatus]);

  // Slider and labels setup
  useEffect(() => {
    const slider = sliderRef.current;
    const labelsContainer = labelsContainerRef.current;

    if (slider && labelsContainer) {
      createLabels(slider, labelsContainer);
      updateSliderBackground(slider);

      const resizeObserver = new ResizeObserver(() => {
        createLabels(slider, labelsContainer);
      });
      resizeObserver.observe(slider);

      return () => resizeObserver.disconnect();
    }
  }, [currentPage, questions]);

  const createLabels = (slider, labelsContainer) => {
    if (!slider || !labelsContainer) return;

    const sliderRect = slider.getBoundingClientRect();
    const min = parseInt(slider.min);
    const max = parseInt(slider.max);

    labelsContainer.innerHTML = "";

    for (let i = min; i <= max; i++) {
      const label = document.createElement("span");
      label.textContent = i;
      label.style.position = "absolute";
      const percent = (i - min) / (max - min);
      const left = percent * sliderRect.width;
      const right = (1 - percent) * sliderRect.width;
      if (i > 6) {
        label.style.right = `${right}px`;
      } else {
        label.style.left = `${left}px`;
      }
      labelsContainer.appendChild(label);
    }
  };

  const updateSliderBackground = (slider) => {
    if (!slider) return;
    const min = slider.min;
    const max = slider.max;
    const val = slider.value;
    slider.style.setProperty("--value", (val - min) / (max - min));
  };

  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    setSliderValue(value);
    updateSliderBackground(e.target);

    if (currentPage > 1 && currentPage <= questions.length + 1) {
      const updatedQuestions = [...questions];
      const currentIndex = currentPage - 2;

      // Maintain consistent answer object structure
      updatedQuestions[currentIndex].answer = {
        ...(updatedQuestions[currentIndex].answer || {}),
        score: value,
      };

      updatedQuestions[currentIndex].answered = true;
      setQuestions(updatedQuestions);
    }
  };
  useEffect(() => {
    const slider = sliderRef.current;
    if (slider && slider.value !== sliderValue) {
      slider.value = sliderValue;
      updateSliderBackground(slider);
    }
  }, [sliderValue]);

  const handleNextPage = async () => {
    const isLastQuestion = currentPage === questions.length + 1;

    if (isLastQuestion) {
      setShowConfirmation(true);
      return;
    }
    if (
      currentPage >= 2 &&
      currentPage <= questions.length + 1 &&
      !isCompleted
    ) {
      const currentQuestion = questions[currentPage - 2];
      const score = sliderValue;




      try {
        const response = await api.post("/user/question/score", {
          user_assessment_score_id: assessmentStatus === 'retake' ? incompleteId : assessmentData.id,
          assessment_question_id: currentQuestion.id,
          score: score,
        });

        const data = response?.data?.data;



        if (data) {
          const updatedQuestions = [...questions];
          updatedQuestions[currentPage - 2].answer = {
            id: data.question_score.id,
            user_assessment_score_id:
              data.question_score.user_assessment_score_id,
            score: data.question_score.score,
          };

          // last question, update full result set
          if (currentPage === totalPages - 1 && response.data.data.questions) {
            setQuestions(response.data.data.questions);
            setScores({
              totalObtainedScore: parseInt(
                response.data.data.total_obtained_score
              ),
              totalScore: parseInt(response.data.data.total_score),
              percentage: Math.round(
                (response.data.data.total_obtained_score /
                  response.data.data.total_score) *
                100
              ),
            });
          } else {
            // Otherwise just update current question's answer
            setQuestions(updatedQuestions);
          }
        }
      } catch (error) {
        console.error(`Error saving question ${currentQuestion?.id}:`, error);
        toast.error(
          `Failed to save response for question ${currentQuestion?.id}`
        );
        return;
      }
    }

    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);

      if (currentPage + 1 >= 2 && currentPage + 1 <= questions.length + 1) {
        const nextQuestion = questions[currentPage - 1];
        const nextScore =
          nextQuestion?.answer?.score ||
          (typeof nextQuestion?.answer === "number" ? nextQuestion.answer : 0);
        setSliderValue(nextScore);
      }
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newCurrentPage = currentPage - 1;
      setCurrentPage(newCurrentPage);

      if (newCurrentPage >= 2 && newCurrentPage <= questions.length + 1) {
        const questionIndex = newCurrentPage - 2;
        const prevQuestion = questions[questionIndex];



        if (prevQuestion) {
          const prevScore =
            typeof prevQuestion.answer === "object"
              ? prevQuestion.answer?.score || 0
              : typeof prevQuestion.answer === "number"
                ? prevQuestion.answer
                : 0;

          setSliderValue(prevScore);

          setTimeout(() => {
            const slider = sliderRef.current;
            if (slider) {
              slider.value = prevScore;
              updateSliderBackground(slider);
            }
          }, 0);
        }
      }
    }
  };
  // Handle yes/no response
  // const handleFinalQuestionAnswered = () => {
  //   setIsFinalQuestion(true);
  //   setShowConfirmation(true);
  // };

  const handleYesNoResponse = async (isYes) => {
    setShowConfirmation(false);

    if (!isYes) {
      return; // Just close the modal if user says No
    }

    // User confirmed - proceed with submitting the last question
    try {
      const currentQuestion = questions[currentPage - 2];
      const score = sliderValue;

      const response = await api.post("/user/question/score", {
        user_assessment_score_id: assessmentStatus == 'retake' ? incompleteId : assessmentData.id,
        assessment_question_id: currentQuestion.id,
        score: score,
      });

      const data = response?.data?.data;

      if (data) {
        const updatedQuestions = [...questions];
        updatedQuestions[currentPage - 2].answer = {
          id: data.id,
          user_assessment_score_id: data.user_assessment_score_id,
          score: data.score,
        };

        // Update questions and scores
        if (response.data.data.questions) {
          setQuestions(response.data.data.questions);
          setScores({
            totalObtainedScore: parseInt(
              response.data.data.total_obtained_score
            ),
            totalScore: parseInt(response.data.data.total_score),
            percentage: Math.round(
              (response.data.data.total_obtained_score /
                response.data.data.total_score) *
              100
            ),
          });
        } else {
          setQuestions(updatedQuestions);
        }

        // Move to results page
        setCurrentPage((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error saving last question:", error);
      toast.error("Failed to save your response");
    }
  };

  const renderQuestionPage = (question) => {
    if (!question) return <div>Question not found</div>;

    return (
      <div className="page">
        <div
          className="d-flex flex-column gap-4 align-items-center"
          style={{ marginBottom: "32px" }}
        >
          <h3
            className="text-body-b2 mb-0"
            style={{
              color: "var(--Text-50, #e6ecf1)",
              textTransform: "capitalize",
            }}
          >
            {assessmentData?.assessment?.name}{" "}
            <span id="current-page">{currentPage - 1}</span>/{questions.length}
          </h3>
          <div className="assessment-top-progress-bar">
            <div
              className="assessment-top-progressed"
              style={{
                width: `${((currentPage - 1) / questions.length) * 100}%`,
              }}
            ></div>
          </div>
        </div>

        <span
          className="text-body-b2"
          style={{
            color: "var(--Text-100, #d0d8df)",
            fontWeight: "bold",
            textTransform: "capitalize",
          }}
        >
          {question.title}
        </span>

        <div
          className="d-flex flex-column mb-4"
          style={{ gap: "12px", marginTop: "44px" }}
        >
          <input
            type="range"
            className="form-range customRange assessment-range"
            min="0"
            max="12"
            step="0"
            value={sliderValue}
            onChange={handleSliderChange}
            ref={sliderRef}
            disabled={isCompleted}
          />
          <div
            className="range-labels d-flex justify-content-between position-relative pb-4"
            style={{ color: "#c8ced3" }}
            ref={labelsContainerRef}
          ></div>
        </div>

        <div className="d-flex flex-column gap-3">
          {/* <h3
            className="text-body-b1 mb-0"
            style={{ color: "var(--Primary-300, #66baff)" }}
          >
            Question Details
          </h3> */}
          <span
            className="text-body-b2"
            style={{ color: "var(--Text-50, #e6ecf1)" }}
            dangerouslySetInnerHTML={{ __html: question.description }}
          ></span>
        </div>

        <div
          className="d-flex justify-content-center gap-3"
          style={{ marginTop: "32px" }}
        >
          <button
            className="btn-2 btn-1-basic text-body-b1 assess-btn prev-button"
            onClick={handlePrevPage}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <mask
                id="mask0_51322_54467"
                maskType="alpha"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="24"
                height="24"
              >
                <rect width="24" height="24" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_51322_54467)">
                <path
                  d="M7.825 13L13.425 18.6L12 20L4 12L12 4L13.425 5.4L7.825 11H20V13H7.825Z"
                  fill="#008CFF"
                />
              </g>
            </svg>
            Previous
          </button>
          <button
            className="btn-2 btn-1-primary text-body-b1 assess-btn next-button"
            onClick={handleNextPage}
          >
            {/* {currentPage === questions.length + 1 ? "Complete" : "Next"} */}
            Next
            <img
              className="rotate-180"
              src="/icons/header-arrow-back.svg"
              alt="next"
            />
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      {loading || assessmentCompleted ? (
        <div className="page-content">
          <div className="assessment-content">
            {/* Empty content while loading or if completed */}
          </div>
        </div>
      ) : (
        <div className="page-content">
          <div className="assessment-content">
            <div
              className="d-flex gap-3 align-items-center mb-3 mb-md-4"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/assessment")}
            >
              <img src="/icons/header-arrow-back.svg" alt="" />
              <h1
                className="text-heading-h2 mb-0"
                style={{ color: "#e6ecf1", textTransform: "capitalize" }}
              >
                {/* {assessmentData?.assessment?.name || "Assessment"} */}
                {currentPage === 1
                  ? assessmentData?.assessment?.name || "Assessment"
                  : "Back to Assessments"}{" "}
              </h1>
            </div>

            <div className="assessment-view">
              {/* <div
                className="d-flex flex-column gap-4 align-items-center"
                style={{ marginBottom: "32px" }}
              >
                <h3
                  className="text-body-b2 mb-0"
                  style={{ color: "var(--Text-50, #e6ecf1)" }}
                >
                  {assessmentData?.assessment?.name}{" "}
                  <span id="current-page">{currentPage}</span>/{totalPages}
                </h3>
                <div className="assessment-top-progress-bar">
                  <div
                    className="assessment-top-progressed"
                    style={{ width: `${(currentPage / totalPages) * 100}%` }}
                  ></div>
                </div>
              </div> */}

              {/* Page 1 - Intro */}
              {currentPage === 1 && (
                <div className="page">
                  {
                    introduction ? (
                      <span className="text-body-b2" style={{ color: "#d0d8df" }} dangerouslySetInnerHTML={{ __html: introduction }}></span>
                    ) :
                      (<span className="text-body-b2" style={{ color: "#d0d8df" }}>
                        <b>4 Dimensions:</b> Body • Mind • Balance • Wealth <br />
                        <b>2 Sections per Dimension:</b> 8 total scoring areas <br />
                        <b>Score each section:</b> 1-12 using the 4 Levels below: <br /><br />
                        <ul>
                          <li>
                            <b>PAIN (1-3)</b> - You're in survival mode. Weak habits, reactive choices, negative patterns dominate.
                          </li>
                          <li>
                            <b>PROGRESS (4-6)</b> - You're trying. Inconsistency is your biggest enemy. Wins happen but don't stick.
                          </li>
                          <li>
                            <b>PURPOSE (7-9)</b> - You're locked in most of the time. Clear intention and discipline drive consistent results.
                          </li>
                          <li>
                            <b>POWER (10-12)</b> - You are weaponised in this area. Standards are non-negotiable and results are automatic.
                          </li>
                        </ul>
                        <br />



                        <b>Max Total:</b> 96 points (12 x 8 sections)
                        <br />
                        <b>Max Per Dimension:</b> 24 points (2 x 12)
                        <br />
                        <br />
                        <b>INSTRUCTIONS:</b>
                        <br />
                        For each section, <b>carefully read all 4 levels</b> and choose the single number that best represents your current reality.
                        <br />
                        The purpose of this assessment is to expose the truth — the good, the bad, and the ugly — so you know exactly where to upgrade.
                        <br />
                        Be brutally honest. If two descriptions feel true, choose the lower score.
                      </span>)
                  }

                  <div className="d-flex justify-content-center">
                    <button
                      className="btn-2 btn-1-primary text-body-b1 next-button"
                      onClick={handleNextPage}
                      style={{
                        borderRadius: "20px",
                        width: "136px",
                        marginTop: "69px",
                        gap: "8px",
                      }}
                    >
                      Begin
                      <img
                        className="rotate-180"
                        src="/icons/header-arrow-back.svg"
                        alt="next"
                      />
                    </button>
                  </div>
                </div>
              )}

              {/* Question pages */}
              {currentPage > 1 &&
                currentPage < totalPages &&
                renderQuestionPage(questions[currentPage - 2])}

              {/* Results page */}
              {currentPage === totalPages && (
                <div className="page">
                  <div
                    className="d-flex flex-column gap-4 align-items-center"
                    style={{ marginBottom: "32px" }}
                  >
                    <h3
                      className="text-body-b2 mb-0"
                      style={{
                        color: "var(--Text-50, #e6ecf1)",
                        textTransform: "capitalize",
                      }}
                    >
                      {assessmentData?.assessment?.name}{" "}
                    </h3>
                    <div className="assessment-top-progress-bar">
                      <div
                        className="assessment-top-progressed"
                        style={{
                          width: `100%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div
                    className="d-flex justify-content-end"
                    style={{ marginBottom: "28px", marginTop: "28px" }}
                  >
                    {/* <button className="btn-4 btn-4-secondary text-body-b3">
                      Download PDF
                    </button> */}
                  </div>
                  <div className="d-flex assessment-view-complete">
                    <div className="selection-complete-assess-card">
                      <div className="your-score-card-container">
                        <div className="your-score-card">
                          <div className="d-flex align-items-center flex-column gap-3">
                            <h1 className="your-score-h1 mb-0">Your Score</h1>
                            <div className="total-score-view">
                              <h1 className="mb-0">
                                {scores.totalObtainedScore} /{" "}
                                {scores.totalScore}
                              </h1>
                            </div>
                            <h3
                              className="text-body-b2 mb-0"
                              style={{
                                fontWeight: "700",
                                color: "#e6ecf1",
                                textAlign: "center",
                              }}
                            >
                              {scores.totalObtainedScore >=
                                Math.floor(scores.totalScore * 0.83)
                                ? "You're inside of prosperity!!"
                                : scores.totalObtainedScore >=
                                  Math.floor(scores.totalScore * 0.5)
                                  ? "You're in abundance!"
                                  : "Keep working - you're in scarcity"}
                            </h3>
                          </div>
                        </div>
                        <div className="your-score-text-container">
                          <h1 className="mb-0">Congratulations!</h1>
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: "6px" }}
                          >
                            <img
                              src="/icons/assesssment-chart-increase.svg"
                              alt=""
                            />
                            <h4 className="mb-0">+{scores.percentage}%</h4>
                          </div>
                          <span
                            className="text-body-b2"
                            style={{ color: "#acb2b7", textAlign: "center" }}
                          >
                            You scored {scores.percentage}% on this assessment.
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="score-data-container">
                      <div className="d-flex flex-column gap-3 mb-4">
                        <h3
                          className="text-body-b2 mb-0"
                          style={{ fontWeight: "700", color: "#e6ecf1" }}
                        >
                          Score Overview
                        </h3>
                        <div
                          className="d-flex gap-2 text-body-b2 flex-wrap"
                          style={{ color: "#acb2b7" }}
                        >
                          <span>
                            0-{Math.floor(scores.totalScore * 0.5)} = Scarcity
                          </span>
                          <span>
                            {Math.floor(scores.totalScore * 0.5) + 1}-
                            {Math.floor(scores.totalScore * 0.83)} = Abundance
                          </span>
                          <span>
                            {Math.floor(scores.totalScore * 0.83) + 1}-
                            {scores.totalScore} = Prosperity
                          </span>
                        </div>
                      </div>
                      <h3
                        className="text-heading-h3 mb-3"
                        style={{ color: "#e6ecf1" }}
                      >
                        Question Scores
                      </h3>
                      <div className="different-score-container">
                        {questions.map((question, index) => {
                          const score =
                            typeof question.answer === "object"
                              ? question.answer.score
                              : typeof question.answer === "number"
                                ? question.answer
                                : 1;

                          return (
                            <div className="score-card" key={index}>
                              <div className="score-for-cards">{score}</div>
                              <h3
                                className="text-body-b3 mb-0"
                                style={{ color: "#c1c9d1" }}
                              >
                                {question.title}
                              </h3>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-3 justify-content-center mt-auto">
                    {/* <button
                      className="btn-2 btn-1-basic assess-btn text-body-b1"
                      style={{ width: "156px", marginTop: "32px" }}
                    >
                      Save
                    </button> */}
                    <button
                      className="btn-2 btn-1-primary assess-btn text-body-b1"
                      style={{ width: "153px", marginTop: "32px" }}
                      onClick={() => navigate("/assessment-view/completed", { state: { id: [assessmentData?.id] } })}
                    >
                      Complete
                      <img
                        className="rotate-180"
                        src="/icons/header-arrow-back.svg"
                        alt=""
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* yes no modal for submission
       */}
      {showConfirmation && (
        <div
          className="modal fade not-completed-journal-modal show"
          id="assessmentConfirmationModal"
          tabIndex="-1"
          aria-labelledby="assessmentConfirmationModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header position-relative">
                <button
                  type="button"
                  className="btn-close btn-close-white position-absolute"
                  style={{ top: "0px", right: "-20px", color: "#e6ecf1" }}
                  aria-label="Close"
                  onClick={() => handleYesNoResponse(false)}
                ></button>
                <h1
                  className="modal-title"
                  id="assessmentConfirmationModalLabel"
                >
                  Submit Assessment
                </h1>
              </div>

              <div className="modal-body">
                <p className="text-body-b2 mb-4">
                  Are you sure you want to submit the assessment?
                </p>
                {/* <p className="text-body-b2 mb-0">
                  You will not be able to edit your answers after submission.
                </p> */}
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px" }}
                    onClick={() => handleYesNoResponse(true)}
                  >
                    Yes
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px" }}
                    onClick={() => handleYesNoResponse(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
